// Updated SpecificationModal.tsx (with TS fixes: Use default empty array for optional categories)
import React, { useState, useEffect } from 'react';
import { scrollToFirstErrorField } from '../../utils/scrollToFirstError';
import { AxiosError } from 'axios';
import { SpecificationDto, CreateSpecificationDto, UpdateSpecificationDto, SpecificationType, CategoryDto } from '../../types';
import { parseAxiosValidationErrors } from '../../utils/productValidation';
import { ValidationError } from './ValidationError';
import { addSpecToCategory, removeSpecFromCategory } from '../../services/api.admin';
import AdminModalShell from './AdminModalShell';

interface SpecificationModalProps {
    isOpen: boolean;
    onClose: () => void;
    isCreating: boolean;
    selectedSpec: SpecificationDto | null;
    createFormData: CreateSpecificationDto;
    setCreateFormData: React.Dispatch<React.SetStateAction<CreateSpecificationDto>>;
    updateFormData: UpdateSpecificationDto;
    setUpdateFormData: React.Dispatch<React.SetStateAction<UpdateSpecificationDto>>;
    handleSaveSpec: () => Promise<SpecificationDto | void>; // Updated to return saved spec for create mode
    categories: CategoryDto[]; // All available categories
}

const SpecificationModal: React.FC<SpecificationModalProps> = ({
    isOpen,
    onClose,
    isCreating,
    selectedSpec,
    createFormData,
    setCreateFormData,
    updateFormData,
    setUpdateFormData,
    handleSaveSpec,
    categories,
}) => {
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [assignedCategories, setAssignedCategories] = useState<string[]>(selectedSpec?.categories ?? []); // Track assigned for UI
    const [pendingAssignments, setPendingAssignments] = useState<{ catId: number; catName: string; isAdd: boolean }[]>([]); // For create mode: track add/remove actions

    useEffect(() => {
        if (isCreating) {
            setAssignedCategories([]);
            setPendingAssignments([]);
        } else if (selectedSpec) {
            setAssignedCategories(selectedSpec.categories ?? []);
            setPendingAssignments([]);
        }
    }, [isCreating, selectedSpec?.id]);

    if (!isOpen) return null;

    const handleSubmit = async () => {
        setErrors({});
        const nameVal = isCreating ? createFormData.name : updateFormData.name ?? selectedSpec?.name ?? '';
        if (!nameVal || !nameVal.trim()) {
            const e: { [k: string]: string } = { Name: 'Name is required' };
            setErrors(e);
            scrollToFirstErrorField(e);
            return;
        }
        try {
            const saved = await handleSaveSpec();
            if (isCreating && saved && pendingAssignments.length > 0) {
                console.log('Applying pending assignments:', pendingAssignments); // Debug: Check if multiple are queued
                for (const assignment of pendingAssignments) {
                    try {
                        if (assignment.isAdd) {
                            const updated = await addSpecToCategory(saved.id, assignment.catId);
                            console.log(`Added category ${assignment.catName}:`, updated); // Debug: Verify backend response includes category
                        } else {
                            const updated = await removeSpecFromCategory(saved.id, assignment.catId);
                            console.log(`Removed category ${assignment.catName}:`, updated);
                        }
                    } catch (assignErr) {
                        console.error(`Failed to assign category ${assignment.catName}:`, assignErr);
                        // Optional: Collect errors but continue
                    }
                }
                setPendingAssignments([]);
            }
            onClose(); // Always close after attempts
        } catch (err: unknown) {
            if (err instanceof AxiosError && err.response) {
                const backendErrors = parseAxiosValidationErrors(err);
                setErrors(backendErrors);
                scrollToFirstErrorField(backendErrors);
            } else if (err instanceof Error) {
                setErrors({ General: 'Save failed: ' + err.message });
            } else {
                setErrors({ General: 'Save failed: Unknown error' });
            }
        }
    };

    const handleCategoryToggle = async (catId: number, catName: string) => {
        const previousAssigned = assignedCategories;
        const isAssigned = previousAssigned.includes(catName);
        const newAssigned = isAssigned
            ? previousAssigned.filter(name => name !== catName)
            : [...previousAssigned, catName];
        setAssignedCategories(newAssigned);

        if (isCreating) {
            setPendingAssignments(prev => {
                const existingIndex = prev.findIndex(p => p.catId === catId);
                if (existingIndex > -1) {
                    return prev.filter((_, i) => i !== existingIndex);
                }
                return [...prev, { catId, catName, isAdd: !isAssigned }];
            });
        } else {
            if (!selectedSpec) return;
            const specId = selectedSpec.id;
            try {
                const updatedSpec = isAssigned
                    ? await removeSpecFromCategory(specId, catId)
                    : await addSpecToCategory(specId, catId);
                setAssignedCategories(updatedSpec.categories ?? []);
            } catch {
                alert('Category assignment failed');
                setAssignedCategories(previousAssigned);
            }
        }
    };

    return (
        <AdminModalShell
            title={isCreating ? 'Create Specification' : 'Edit Specification'}
            onClose={onClose}
            size="md"
        >
            <ValidationError error={errors.General} />
            <div className="grid grid-cols-1 gap-4">
                <div data-error-field="Name">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                        type="text"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm"
                        value={isCreating ? createFormData.name : updateFormData.name ?? selectedSpec?.name ?? ''}
                        onChange={(e) => {
                            if (isCreating) setCreateFormData({ ...createFormData, name: e.target.value });
                            else setUpdateFormData({ ...updateFormData, name: e.target.value });
                        }}
                    />
                    <ValidationError error={errors.Name} />
                </div>
                <div data-error-field="Type">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm"
                        value={isCreating ? createFormData.type : updateFormData.type ?? selectedSpec?.type ?? SpecificationType.String}
                        onChange={(e) => {
                            const value = e.target.value as SpecificationType;
                            if (isCreating) setCreateFormData({ ...createFormData, type: value });
                            else setUpdateFormData({ ...updateFormData, type: value });
                        }}
                    >
                        {Object.values(SpecificationType).map(type => <option key={type} value={type}>{type}</option>)}
                    </select>
                    <ValidationError error={errors.Type} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Is Required</label>
                    <input
                        type="checkbox"
                        checked={isCreating ? createFormData.isRequired : updateFormData.isRequired ?? selectedSpec?.isRequired ?? false}
                        onChange={(e) => {
                            if (isCreating) setCreateFormData({ ...createFormData, isRequired: e.target.checked });
                            else setUpdateFormData({ ...updateFormData, isRequired: e.target.checked });
                        }}
                    />
                    <ValidationError error={errors.IsRequired} />
                </div>
                <div data-error-field="Options">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Options (comma-separated for Enum)</label>
                    <input
                        type="text"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm"
                        value={isCreating ? createFormData.options ?? '' : updateFormData.options ?? selectedSpec?.options ?? ''}
                        onChange={(e) => {
                            if (isCreating) setCreateFormData({ ...createFormData, options: e.target.value });
                            else setUpdateFormData({ ...updateFormData, options: e.target.value });
                        }}
                    />
                    <ValidationError error={errors.Options} />
                </div>
                <div> {/* Show in both modes */}
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assign to Categories</label>
                    <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-2">
                        {categories.map(cat => (
                            <div key={cat.id} className="flex items-center mb-2">
                                <input
                                    type="checkbox"
                                    id={`cat-${cat.id}`}
                                    checked={assignedCategories.includes(cat.name)}
                                    onChange={() => handleCategoryToggle(cat.id, cat.name)}
                                    className="mr-2"
                                />
                                <label htmlFor={`cat-${cat.id}`} className="text-sm text-gray-900">{cat.name}</label>
                            </div>
                        ))}
                    </div>
                    {assignedCategories.length === 0 && <p className="text-gray-500 text-sm mt-2">No categories assigned yet.</p>}
                </div>
            </div>
            <div className="mt-6 flex justify-end">
                <button onClick={handleSubmit} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium mr-3">Save</button>
                <button onClick={onClose} className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors font-medium">Cancel</button>
            </div>
        </AdminModalShell>
    );
};

export default SpecificationModal;